import { useEffect, useMemo, useState } from "react";
import { BorderedButton } from "../../../commonComponents";
import { getToken } from "../../../../utils/localStorage";
import start from "../../../../assets/images/sort_icon.png";
import filter from "../../../../assets/images/Filter-icon.svg";
import { useScreenWidth } from "../../../../utils/useScreenWidth";
import { Table, Popover, Checkbox, Slider, Radio, Spin } from "antd";
import { getLeagueImage } from "../../../../helpers/leagueLogo";
import { ReactComponent as FootballIcon } from "../../../../assets/images/football-icon.svg";
import { ReactComponent as BasketBallIcon } from "../../../../assets/images/basketball_icon.svg";
import {
  getStartedPublicGamesForPublic,
  getPublicGamesForUserFilter,
  getStartedPublicGamesForPublicFilter,
} from "../../../../utils/service";
import "./PastGameTable.scss";
import { Link } from "react-router-dom";

const potCheckboxOptions = [
  { label: "Lowest - Highest", value: "low_to_high" },
  { label: "Highest - Lowest", value: "high_to_low" },
];

const statusCheckboxOptions = [
  { label: "Completed", value: true },
  { label: "Ongoing", value: false },
];

const PastGameTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [gameTableData, setGameTableData] = useState();
  const [allGamesData, setAllGamesData] = useState([]);
  const [filterData, setFilterData] = useState();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const screenWidth = useScreenWidth();
  const isUser = getToken();
  const [noGame, setNoGame] = useState(null);

  // All derived from allGamesData (the unfiltered fetch), never from gameTableData
  // (which becomes the filtered result set once a filter is applied) — otherwise a
  // player filtering down to one result would watch the filters disappear and have
  // no way to undo their own filter.
  const hasEnoughGames = allGamesData.length >= 2; // Rule 1: outer gate
  const distinctSportCount = new Set(allGamesData.map((game) => game.sport_id)).size;
  const distinctLeagueCount = new Set(allGamesData.map((game) => game.season_id)).size;
  const hasMultipleSports = distinctSportCount >= 2; // Rule 2: Sport filter
  const hasMultipleLeagues = distinctLeagueCount >= 2; // Rule 2: League filter
  const hasBothStatuses =
    allGamesData.some((game) => game.is_finished) &&
    allGamesData.some((game) => !game.is_finished); // Rule 2: Status filter

  // League dropdown options: leagues actually present in this (Started) tab's games
  // (allGamesData, same unfiltered-source rule as above), deduped and sorted
  // alphabetically. Recomputes independently from the Upcoming tab's list — each tab
  // is a separate component instance (Game.jsx renders the Tabs with
  // destroyInactiveTabPane, so switching tabs unmounts/remounts and resets this
  // component's state), so there's no stale cross-tab selection to worry about.
  // Previously this came from a separate "all leagues" backend endpoint
  // (gameLeaguesData/GET /leagues), still used elsewhere (Insights, PrivateGame
  // creation) and left untouched — this dropdown just stops reading from it.
  const leagueOptions = useMemo(() => {
    const names = new Set(
      allGamesData.map((game) => game.league_name).filter(Boolean)
    );
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allGamesData]);

  // If a previously-checked league drops out of leagueOptions (e.g. the underlying
  // game list refreshes), drop it from the active filter too — see GameTable.jsx for
  // the matching guard on the Upcoming tab.
  useEffect(() => {
    setFilterData((prevFilterData) => {
      if (!prevFilterData?.competition?.length) return prevFilterData;
      const stillValid = prevFilterData.competition.filter((league) =>
        leagueOptions.includes(league)
      );
      if (stillValid.length === prevFilterData.competition.length) {
        return prevFilterData;
      }
      return { ...prevFilterData, competition: stillValid };
    });
  }, [leagueOptions]);

  const handleSportsCheckbox = (checkedValues) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      sports: checkedValues,
    }));
  };
  const handleStatusCheckbox = (checkedValues) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      finished: checkedValues,
    }));
  };
  const handleCompetitonsCheckbox = (checkedValues) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      competition: checkedValues,
    }));
  };
  const handlePotValue = (e) => {
    setFilterData((prevFilterData) => {
      const newFilterData = { ...prevFilterData };
      if ("stake" in newFilterData) {
        delete newFilterData.stake;
      }
      newFilterData.pot = e.target.value;
      return newFilterData;
    });
  };

  const handleStatusValue = (e) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      finished: e.target.value,
    }));
  };

  const getGameTableData = async () => {
    const response = await getStartedPublicGamesForPublic(currentPage);
    const modifiedGameData = response?.data?.games?.map((game) => ({
      ...game,
      key: game.id,
    }));
    if (modifiedGameData?.length == 0) {
      setNoGame(true);
    } else setNoGame(false);
    setGameTableData(modifiedGameData);
    setAllGamesData(modifiedGameData || []);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const competitionsContent = (
    <div>
      <Checkbox.Group
        options={leagueOptions}
        onChange={handleCompetitonsCheckbox}
        className="game-table-checkbox"
      />
    </div>
  );
  const sportsContent = (
    <div>
      <Checkbox.Group
        onChange={handleSportsCheckbox}
        className="game-table-checkbox"
      >
        <Checkbox
          value="football"
          className="game-table-sports-checkboxes-item"
        >
          {/* <FootballIcon className="game-table-sports-checkboxes-image iconFootbll" /> */}
          Football
        </Checkbox>
        <Checkbox
          value="basketball"
          className="game-table-sports-checkboxes-item"
        >
          {/* <BasketBallIcon className="game-table-sports-checkboxes-image iconFootbll" /> */}
          Basketball
        </Checkbox>
      </Checkbox.Group>
    </div>
  );

  const potContent = (
    <Radio.Group
      options={potCheckboxOptions}
      onChange={handlePotValue}
      className="game-table-stakeradio"
    />
  );

  const statusContent = (
    <Radio.Group
      options={statusCheckboxOptions}
      value={filterData?.finished}
      onChange={handleStatusValue}
      className="game-table-stakeradio"
    />
  );
  const columns = [
    {
      title: "Game Ref",
      dataIndex: "title",
      width: "120px",
      render: (_, record) => {
        return (
          <div>
            {record.is_turbo && (
              <div className="game-table-turbotag font-cairo-bold">Turbo</div>
            )}
            <div>{record.title}</div>
          </div>
        );
      },
    },
    {
      title: hasEnoughGames && hasMultipleSports ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-sports"
          content={sportsContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Sport
            <img className="filterStyle" src={filter} />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Sport
        </div>
      ),
      dataIndex: "sport_id",
      render: (_, record) => {
        return (
          <div className="game-table-sports-cont">
            {record.sport_name.toLowerCase() == "football" ? (
              <FootballIcon className="game-table-sports-cont-icon" />
            ) : (
              <BasketBallIcon className="game-table-sports-cont-icon" />
            )}
            {record.sport_name}
          </div>
        );
      },
    },
    {
      title: hasEnoughGames && hasMultipleLeagues ? (
        <Popover
          content={competitionsContent}
          placement="bottom"
          trigger={["click"]}
          overlayClassName="game-table-popover-menu game-table-competitions"
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            League
            <img className="filterStyle" src={filter} />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          League
        </div>
      ),
      dataIndex: "competitions",
      render: (_, record) => {
        return (
          <div className="game-table-competitions-items">
            <div className="game-table-competitions-items-image">
              <img
                src={getLeagueImage(record.league_name, record.league_image)}
                alt="League Icon"
                className="game-table-competitions-items-icon"
                style={{ width: record?.sport_id == 2 ? "17px" : "34px" }}
              />
            </div>

            <div>{record.league_name}</div>
          </div>
        );
      },
    },
    {
      title: hasEnoughGames ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-pot"
          content={potContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Pot
            <img className="filterStyle" src={start} alt="Filter Icon" />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Pot
        </div>
      ),
      dataIndex: "pot",
      render: (_, record) => {
        return <div className="filterPadding">${Math.floor(record.pot).toLocaleString('en-US')}</div>;
      },
    },
    {
      title: "Entries",
      dataIndex: "remaining_players",
      render: (_, record) => {
        // Left: remaining_players - entries still in the game. While a round
        // is live it is the current round's entries (same figure as the
        // dashboard's "Entries Remaining"); once the game settles it is the
        // winner count (1 for a single winner, N for an N-way split).
        // Right: max_players - total entries in the game (round 1 count).
        // Hides the whole cell if either side is missing rather than
        // showing a half-filled fraction.
        if (record?.remaining_players == null || record?.max_players == null) {
          return null;
        }
        return (
          <div className="ongoing-table-entries-cont justify-content-center">
            {record?.remaining_players
              ?.toString()
              ?.split("")
              .map((item, index) => {
                return (
                  <div
                    key={index}
                    className="ongoing-table-entries-cont-number container-border font-cairo-bold"
                  >
                    {item}
                  </div>
                );
              })}
            <div className="ongoing-table-entries-centerline"></div>
            {record?.max_players
              ?.toString()
              ?.split("")
              .map((item, index) => {
                return (
                  <div
                    key={index}
                    className="ongoing-table-entries-cont-number container-border font-cairo-bold"
                  >
                    {item}
                  </div>
                );
              })}
          </div>
        );
      },
    },
    {
      title: hasEnoughGames && hasBothStatuses ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-sports"
          content={statusContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Status
            <img className="filterStyle" src={filter} />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Status
        </div>
      ),
      dataIndex: "sport_id",
      render: (_, record) => {
        return (
          <div className="game-table-sports-cont justify-content-center">
            {record.is_finished ? "Completed" : "Ongoing"}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "key",
      key: "key",
      render: (_, record) => {
        return (
          <div onClick={() => {}}>
            <BorderedButton
              name={"View Game"}
              link={`/allselections/${record.id}?title=${record.title}`}
              btnHeight={"37px"}
              btnClass={"game-table-action-button"}
              dataToSend={{
                id: record.id,
                title: record.title,
                ...(record.is_finished && { round: record.round_number }),
              }}
            />
          </div>
        );
      },
    },
  ];

  const handleFilteredGameData = async () => {
    const queryParams = Object.entries(filterData)
      .filter(([key, value]) => {
        return (
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === "string" && value !== "") ||
          (key === "finished" && typeof value === "boolean")
        );
      })
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const encodedArray = value
            .map((v) => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`)
            .join("&");
          return encodedArray;
        } else if (key === "finished") {
          return `${encodeURIComponent(key)}=${value}`;
        } else {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      })
      .join("&");

    const response = await getStartedPublicGamesForPublicFilter(
      currentPage,
      queryParams
    );
    if (response.games) {
      const modifiedGameData = response?.games?.map((game) => ({
        ...game,
        key: game.id,
      }));
      setGameTableData(modifiedGameData);
    }
  };

  useEffect(() => {
    getGameTableData();
  }, [currentPage, isUser]);

  useEffect(() => {
    if (filterData) {
      handleFilteredGameData();
    }
  }, [filterData]);

  return (
    <>
      {noGame === null ? (
        <Spin />
      ) : !noGame ? (
        <>
          <div
            className={`${
              screenWidth >= 992 && "game-table container-border"
            } position-relative background-blur`}
          >
            {screenWidth >= 992 ? (
              <Table
                columns={columns}
                dataSource={gameTableData}
                onRow={(record) => ({
                  onClick: () => {
                    const newExpandedRowKeys = expandedRowKeys.includes(
                      record.key
                    )
                      ? []
                      : [record.key];
                    setExpandedRowKeys(newExpandedRowKeys);
                  },
                })}
                pagination={false}
                // pagination={{
                //   current: currentPage,
                //   total: gameTableData?.total_records,
                //   onChange: handlePageChange,
                // }}
              />
            ) : (
              <>
                {hasEnoughGames && (
                <div className="popOverContainerMain">
                  {hasMultipleSports && (
                  <div className="game-table-filter-grid game-table-filter-grid--single">
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-sports"
                        content={sportsContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Sport
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                  </div>
                  )}
                  {/* Same grid as the Upcoming tab (GameTable.jsx / GameTable.scss):
                      League, Pot, Status share game-table-filter-card's width. */}
                  <div className="game-table-filter-grid">
                    {hasMultipleLeagues && (
                    <div className="game-table-filter-card">
                      <Popover
                        content={competitionsContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                        overlayClassName="game-table-popover-menu game-table-competitions"
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          League
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    )}
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-pot"
                        content={potContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Pot
                          <img
                            className="filterStyle"
                            src={start}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    {hasBothStatuses && (
                    <div className="game-table-filter-card">
                      <Popover
                        content={statusContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                        overlayClassName="game-table-popover-menu game-table-competitions"
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Status
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    )}
                  </div>
                </div>
                )}
                <div className="gametable-mobile popOverMai">
                  {gameTableData?.map((item, index) => {
                    return (
                      <>
                        <div
                          className="gametable-mobile-title font-cairo-bold"
                          key={index + 1}
                        >
                          {item.title}
                        </div>
                        <div className="gametable-mobile-cont" key={index}>
                          <div className="gametable-mobile-cont-items">
                            {item?.is_turbo && (
                              <div className="game-table-turbotag font-cairo-bold">
                                Turbo
                              </div>
                            )}
                            <div className="gametable-mobile-cont-items-title">
                              League
                            </div>
                            <div className="gametable-mobile-cont-items-icon">
                              <img
                                src={getLeagueImage(item.league_name, item.league_image)}
                                alt="League Icon"
                                className="image-cover-full"
                              />
                            </div>
                            <div
                              className={
                                item?.is_turbo
                                  ? "gametable-mobile-cont-items-league-name-turbo"
                                  : undefined
                              }
                            >
                              {item.league_name}
                            </div>
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Sport
                            </div>
                            <div>{item.sport_name}</div>
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Pot
                            </div>
                            <div>${Math.floor(item.pot).toLocaleString('en-US')}</div>
                          </div>
                          {(item?.remaining_players != null && item?.max_players != null) && (
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Entries
                            </div>
                            {/* remaining_players / max_players - same
                                fields and same hide rule as the desktop
                                column above. */}
                            <div className="ongoing-table-entries-cont justify-content-center">
                              {item?.remaining_players
                                ?.toString()
                                ?.split("")
                                .map((digit, index) => {
                                  return (
                                    <div
                                      key={index}
                                      className="ongoing-table-entries-cont-number container-border font-cairo-bold"
                                    >
                                      {digit}
                                    </div>
                                  );
                                })}
                              <div className="ongoing-table-entries-centerline"></div>
                              {item?.max_players
                                ?.toString()
                                ?.split("")
                                .map((digit, index) => {
                                  return (
                                    <div
                                      key={index}
                                      className="ongoing-table-entries-cont-number container-border font-cairo-bold"
                                    >
                                      {digit}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                          )}
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Status
                            </div>
                            <div>
                              {item?.is_finished ? "Completed" : "Ongoing"}
                            </div>
                          </div>
                          <div className="gametable-mobile-cont-items justify-center">
                            <BorderedButton
                              name={"View Game"}
                              link={`/allselections/${item.id}?title=${item.title}`}
                              btnHeight={"54px"}
                              btnClass={"past-mobile-button"}
                              dataToSend={{
                                id: item.id,
                                title: item.title,
                                ...(item?.is_finished && { round: item.round_number }),
                              }}
                              // btnClass={"gametable-mobile-button"}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="game-table-nodata font-cairo-medium">
          No Games to Display.
        </div>
      )}
    </>
  );
};
export default PastGameTable;
